import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import track from "@/lib/track";

const API = process.env.REACT_APP_BACKEND_URL;
const SEGUIR_MIN_SEG = 30;   // el video es obligatorio: SEGUIR recién aparece a los 30 s (o al terminar)

// Video de bienvenida: una sola vez, post-registro, pantalla completa, SIN autoplay.
// Soporta URL (Loom/YouTube/Vimeo) o archivo. SEGUIR aparece a los 30 s (o al terminar); NO hay "saltear".
// El "visto" se guarda AL CONTINUAR/finalizar (no al mostrar): se espera el POST y recién ahí se cierra.
// Si el guardado falla, se muestra el error con reintento (no queda bloqueado). Si el embed no da señal en
// 6 s, o el video falla/cuelga, se ofrece una salida recuperable (no un cierre silencioso ni un bucle).
// `manual`: se muestra a pedido desde el menú de la cuenta (no marca visto ni registra eventos).
export default function VideoBienvenida({ onDone, manual = false }) {
  const [info, setInfo] = useState(null);
  const [visible, setVisible] = useState(false);
  const [puedeSeguir, setPuedeSeguir] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState(false);
  const [fallo, setFallo] = useState(false);           // el embed no dio señal / el video falló o colgó
  const cerrado = useRef(false);
  const enviando = useRef(false);   // operación de guardado en curso (síncrono, evita reenvío por 'ended')
  const reproducido = useRef(false);
  const terminado = useRef(false);
  const cargadoRef = useRef(false);
  const ultimoSeg = useRef(0);
  const loadTimer = useRef(null);
  const visTimer = useRef(null);
  const visSeg = useRef(0);
  const iframeRef = useRef(null);

  const limpiarTimers = () => {
    if (loadTimer.current) { clearTimeout(loadTimer.current); loadTimer.current = null; }
    if (visTimer.current) { clearInterval(visTimer.current); visTimer.current = null; }
  };

  // Cuenta 30 s de reproducción VISIBLE cuando no llegan eventos del player (Loom/player.js).
  // No cuenta con la pestaña oculta.
  const arrancarConteoVisible = () => {
    if (visTimer.current || puedeSeguir || manual) return;
    visTimer.current = setInterval(() => {
      if (document.visibilityState === "visible") {
        visSeg.current += 1;
        if (visSeg.current >= SEGUIR_MIN_SEG) {
          setPuedeSeguir(true);
          if (visTimer.current) { clearInterval(visTimer.current); visTimer.current = null; }
        }
      }
    }, 1000);
  };

  const marcarCargado = () => {
    cargadoRef.current = true;
    if (loadTimer.current) { clearTimeout(loadTimer.current); loadTimer.current = null; }
    arrancarConteoVisible();
  };

  // Cargar los datos del video. Sin video o error de carga → pantalla de recuperación (no cierra ni guarda).
  const cargar = async () => {
    cargadoRef.current = false;   // NO bajar `fallo` acá: durante el await, info sigue null y no debe
    try {                         // entrar a la rama de video. `fallo` se baja recién con un bi válido.
      const { data } = await api.get("/clips-especiales");
      const bi = data.clips_info?.bienvenida;
      const yaVista = !!data.bienvenida_vista;
      if (manual) {
        if (!bi) { cerrado.current = true; onDone && onDone(); return; }
        setInfo(bi); setFallo(false); setVisible(true); setPuedeSeguir(true); return;
      }
      if (yaVista) { cerrado.current = true; onDone && onDone(); return; }
      if (!bi) {
        // No hay video configurado: mostramos recuperación; el "visto" se guarda SOLO si elige continuar.
        setInfo(null); setVisible(true); setFallo(true); return;
      }
      setInfo(bi); setFallo(false); setVisible(true);
      track("bienvenida_mostrada", { proveedor: bi.proveedor || bi.tipo });
      // Si el embed no da NINGUNA señal de carga real en 6 s → recuperación (no depende del hover).
      loadTimer.current = setTimeout(() => { if (!cargadoRef.current) setFallo(true); }, 6000);
    } catch {
      // Error al leer los datos: recuperación explícita, sin cerrar (no deja OAuth pendiente sin salida).
      setInfo(null); setVisible(true); setFallo(true);
    }
  };

  useEffect(() => {
    cargar();
    return () => { limpiarTimers(); };
    // eslint-disable-next-line
  }, []);

  // Guarda el "visto" y recién entonces cierra. `enviando` (ref) se pone ANTES del await para que un
  // 'ended' del player no dispare un segundo POST mientras el primero está en vuelo. Reintento ante error.
  const finalizar = async (motivo) => {
    if (cerrado.current || enviando.current) return;
    if (manual) {
      cerrado.current = true; limpiarTimers(); onDone && onDone(); return;
    }
    enviando.current = true;
    setGuardando(true); setErrorGuardar(false);
    try {
      await api.post("/visto/bienvenida");
    } catch {
      enviando.current = false; setGuardando(false); setErrorGuardar(true); return;   // NO cerrar: reintento
    }
    cerrado.current = true; limpiarTimers();
    if (motivo === "terminada" && !terminado.current) {
      terminado.current = true; track("bienvenida_terminada", { segundo: ultimoSeg.current });
    } else if (motivo === "salteada" && !terminado.current) {
      track("bienvenida_salteada", { segundo: ultimoSeg.current });
    }
    onDone && onDone();
  };

  // Eventos del reproductor por protocolo player.js (Loom / Vimeo).
  useEffect(() => {
    if (!visible || info?.tipo !== "url") return;
    const onMsg = (e) => {
      const d = e.data;
      if (!d || typeof d !== "object" || d.context !== "player.js") return;
      marcarCargado();
      if (d.event === "ready") {
        const win = iframeRef.current?.contentWindow;
        if (win) ["play", "ended", "timeupdate"].forEach((ev) =>
          win.postMessage({ context: "player.js", method: "addEventListener", value: ev }, "*"));
      } else if (d.event === "play") {
        if (!reproducido.current) { reproducido.current = true; if (!manual) track("bienvenida_reproducida", {}); }
      } else if (d.event === "timeupdate") {
        const s = d.value?.seconds ?? d.data?.seconds;
        if (typeof s === "number") {
          ultimoSeg.current = Math.round(s * 10) / 10;
          if (s >= SEGUIR_MIN_SEG) setPuedeSeguir(true);
        }
      } else if (d.event === "ended") {
        setPuedeSeguir(true);
        if (reproducido.current) finalizar("terminada");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line
  }, [visible, info, manual]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex flex-col items-center justify-center bg-negro px-6" data-testid="video-bienvenida">
      <div className="w-full" style={{ maxWidth: 800 }}>
        <div className="mono-label text-dorado text-center mb-4" data-testid="video-bienvenida-titulo" style={{ letterSpacing: "0.14em" }}>
          Antes de arrancar, dos minutos.
        </div>

        {fallo ? (
          <div className="card-dark p-6 text-center" data-testid="video-bienvenida-fallo">
            <p className="text-crema text-sm mb-5">No pudimos cargar el video. Podés reintentar o continuar igual.</p>
            <div className="flex items-center justify-center gap-3">
              <button className="btn-fantasma" data-testid="video-bienvenida-reintentar"
                onClick={() => {
                  if (info) { setFallo(false); cargadoRef.current = false;
                    if (iframeRef.current) { const s = iframeRef.current.src; iframeRef.current.src = s; }
                    loadTimer.current = setTimeout(() => { if (!cargadoRef.current) setFallo(true); }, 6000);
                  } else { cargar(); }   // sin video configurado: reintentar la carga de datos
                }}>
                REINTENTAR
              </button>
              <button className="btn-dorado" data-testid="video-bienvenida-continuar-igual" disabled={guardando}
                onClick={() => finalizar("salteada")}>
                {guardando ? "GUARDANDO…" : "CONTINUAR IGUAL"}
              </button>
            </div>
            {errorGuardar && (
              <p className="text-[#EC835A] text-xs mt-4" data-testid="video-bienvenida-error">
                No pudimos guardar. Reintentá en un momento.
              </p>
            )}
          </div>
        ) : (
          <>
            <div
              className="relative w-full rounded-2xl overflow-hidden"
              style={{ paddingBottom: "56.25%", background: "#000", border: "1px solid rgba(201,168,76,0.35)" }}
            >
              {info.tipo === "url" ? (
                <iframe
                  ref={iframeRef}
                  src={info.embed_url}
                  title="Video de bienvenida"
                  frameBorder="0"
                  allow="fullscreen; picture-in-picture"
                  allowFullScreen
                  onLoad={() => marcarCargado()}
                  className="absolute top-0 left-0 w-full h-full"
                  data-testid="video-bienvenida-iframe"
                />
              ) : (
                <video
                  src={`${API}/api/clip-video/${info.id}`}
                  controls
                  playsInline
                  onCanPlay={marcarCargado}
                  onPlay={() => { if (!reproducido.current) { reproducido.current = true; if (!manual) track("bienvenida_reproducida", {}); } arrancarConteoVisible(); }}
                  onTimeUpdate={(e) => { const ct = e.target.currentTime || 0; ultimoSeg.current = Math.round(ct * 10) / 10; if (ct >= SEGUIR_MIN_SEG) setPuedeSeguir(true); }}
                  onEnded={() => { setPuedeSeguir(true); finalizar("terminada"); }}
                  onError={() => setFallo(true)}
                  className="absolute top-0 left-0 w-full h-full"
                  data-testid="video-bienvenida-player"
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-4 mt-7">
              {errorGuardar && (
                <span className="text-[#EC835A] text-xs" data-testid="video-bienvenida-error">
                  No pudimos guardar. Reintentá.
                </span>
              )}
              {puedeSeguir ? (
                <button className="btn-dorado" onClick={() => finalizar("terminada")} disabled={guardando} data-testid="video-bienvenida-seguir">
                  {guardando ? "GUARDANDO…" : "SEGUIR"}
                </button>
              ) : (
                <span className="mono-label text-gris" data-testid="video-bienvenida-espera" style={{ fontSize: 12, letterSpacing: "0.08em" }}>
                  MIRÁ EL VIDEO PARA SEGUIR
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
